/**
 * What the Recipe Kitchen can cook, and what it is allowed to put on the shelf
 * (specs/recipe-mode.md §6).
 *
 * A recipe is a list of INGREDIENT IDS and nothing else: the card's text, the shelf's pictures and
 * the answer set are all derived from the same list, so they cannot come to disagree. Ids are
 * language-neutral, exactly as the picture-word set is — a tomato is a tomato in ten locales — and
 * the only per-language content in the whole mode is the dish names and the ten ingredient words
 * this mode had to add (i18n `recipe.dish.*` / `recipe.ing.*`).
 *
 * Two ID SPACES meet here, and the difference decides both the picture and the word:
 *
 *  - **Pool ids** (`food-bread`, `fruit-apple`, `vegetables-tomato`) are picture-word words. Their
 *    icon is the shipped `/images/words/<id>.webp` and their NAME is the picture pool's own text
 *    for the active language — already authored, already reviewed, in all ten locales. Free.
 *  - **`ing-*` ids** are this mode's own eight (flour, butter, sugar, salt, water, noodles, rice,
 *    chocolate). Nothing in the game had a word or a picture for them, so they carry their own art
 *    in `/images/recipe/` and their own i18n keys.
 *
 * The pool wins wherever it has the word, so a child who meets *Tomate* in the picture journey
 * reads the same *Tomate* here. That is worth two id spaces.
 */

/** A dish the kitchen can produce, and the exact set of things that make it. */
export interface Recipe {
  /** Suffix of the art (`dish-<id>.webp`) and of the i18n key (`recipe.dish.<id>`). */
  id: string;
  /** The answer set, and the card, in the order the card prints them. */
  ingredients: string[];
  /**
   * The languages this dish belongs to, by base code (`['ja']`). **Absent means every language** —
   * the shared book, which is most of it.
   *
   * A national dish is not decoration: a Thai child cooking Pad Thai and a French child cooking a
   * croissant are reading their own kitchen, which is the difference between a word list and
   * something worth finishing. It also means the book has a different SIZE per language, so
   * anything that counts recipes (the master-chef card, §5.5) has to count the language's own.
   */
  langs?: string[];
  /**
   * Ids that must never be dealt as DECOYS for this dish — the rule that makes the mode
   * single-answer (§6.5).
   *
   * Without it the mode punishes exactly the child who is thinking: onion on a pizza, milk in a
   * cake and lemon in a fruit salad are all right answers to the wrong question, and "it is not on
   * the card" is a thin defence when the picture shows a pizza. Each list therefore holds
   * everything the pictured dish plausibly contains but this recipe does not ask for — plus the
   * dish's own picture-word twin where it has one (`food-pizza` while cooking a pizza), which is
   * not a plausible ingredient but is a plausible misread.
   */
  forbid: string[];
}

/**
 * Picture-pool words that may be dealt as INGREDIENTS.
 *
 * Deliberately not "everything edible in the pool": the six `food-*` words that are themselves
 * finished dishes are excluded here and appear only as decoys (see DECOY_ONLY) — a pizza is not an
 * ingredient. Corn is excluded outright (see NEVER_DEAL).
 */
export const POOL_INGREDIENTS: readonly string[] = [
  'food-bread', 'food-cheese', 'food-egg', 'food-milk',
  'fruit-apple', 'fruit-banana', 'fruit-orange', 'fruit-pear', 'fruit-grapes',
  'fruit-strawberry', 'fruit-lemon', 'fruit-cherry', 'fruit-watermelon', 'fruit-pineapple',
  'vegetables-carrot', 'vegetables-tomato', 'vegetables-potato', 'vegetables-onion',
  'vegetables-cucumber', 'vegetables-pepper', 'vegetables-pumpkin', 'vegetables-broccoli',
  'vegetables-pea',
];

/**
 * This mode's own eight, in `/images/recipe/` with their own i18n words (§6.3).
 *
 * They are not picture-word words and deliberately do not become any: the word pool is pinned at
 * 150 words by two tests and its icon directory sits inside 4 KB of an 800 KB budget, so ten more
 * words there is a different change than it looks — one that moves the picture lesson's star pacing
 * and its download size. This mode carries its own art instead, and pays for it in one directory.
 */
export const OWN_INGREDIENTS: readonly string[] = [
  'ing-flour', 'ing-butter', 'ing-sugar', 'ing-salt',
  'ing-water', 'ing-noodles', 'ing-rice', 'ing-chocolate',
  // The second five, added with the national dishes: sushi without fish and a burger without a
  // patty are not the dishes a child would recognise, and those two are the only animal
  // ingredients in the book (see the note on RECIPES).
  'ing-fish', 'ing-seaweed', 'ing-mango', 'ing-oil', 'ing-meat',
  // Garlic came with the fondue, and it is the one ingredient that may never be a WRONG answer —
  // see NEVER_DECOY.
  'ing-garlic',
];

/**
 * Finished dishes from the picture pool. Never an ingredient, always a legal decoy: a cake on the
 * shelf while the card asks for flour and eggs is a good wrong answer, and a real one.
 */
export const DECOY_ONLY: readonly string[] = [
  'food-pizza', 'food-soup', 'food-cake', 'food-icecream', 'food-sandwich', 'food-cookie',
];

/**
 * The shelf's whole inventory: everything that may ever appear on a tile, as an ingredient or as a
 * decoy.
 */
export const SHELF_INVENTORY: readonly string[] = [
  ...POOL_INGREDIENTS, ...OWN_INGREDIENTS, ...DECOY_ONLY,
];

/**
 * Ids that may not appear on the shelf at all, whatever else is true.
 *
 * **Corn is the lives.** 🌽 is what a wrong pick costs (specs/recipe-mode.md §5.2), and
 * `vegetables-corn` ships as a picture-word icon — so without this line the shelf would eventually
 * offer a child one of their own lives to cook, which is the exact confusion two glyphs exist to
 * prevent. Enforced in the dealer and pinned by recipes.test.ts rather than left to the authoring
 * of the lists above.
 */
export const NEVER_DEAL: ReadonlySet<string> = new Set(['vegetables-corn']);

/**
 * Ids that may be an ANSWER but never a decoy.
 *
 * Garlic is the case this exists for: it goes into most savoury dishes in the book, so as a wrong
 * tile it would break the rule §6.5 rests on — a decoy may never be something the pictured dish
 * plausibly contains. The alternative was adding `ing-garlic` to fifteen separate ban lists and
 * hoping the sixteenth recipe remembered; a single line that says "this is never a fair wrong
 * answer" is the honest version of the same thing.
 */
export const NEVER_DECOY: ReadonlySet<string> = new Set(['ing-garlic']);

/**
 * The eighteen. Three to five ingredients each; the round table (`RECIPE.rounds` in game/config.ts)
 * decides which sizes a given rung may draw — eight of three, eight of four, two of five.
 *
 * Free of anything needing a knife and a flame, and animal ingredients are held to exactly two —
 * `ing-fish` and `ing-meat` — which exist because sushi without fish and a burger without a patty
 * are not the dishes a child would recognise. Nothing else in the book needs them. Not squeamishness:
 * the audience is 5–8 and ten locales wide, and a shelf that works in as many kitchens as possible
 * is one less thing for a native reviewer to send back.
 *
 * **Most of the book is shared; four languages have a shelf of their own.** A recipe with no `langs`
 * is dealt everywhere; `langs: ['ja']` is dealt only in Japanese. See `recipesFor`.
 */
export const RECIPES: readonly Recipe[] = [
  {
    id: 'spaghetti',
    ingredients: ['ing-noodles', 'vegetables-tomato', 'food-cheese'],
    forbid: ['vegetables-onion', 'vegetables-pepper', 'ing-salt', 'ing-butter', 'vegetables-broccoli'],
  },
  {
    id: 'pancakes',
    ingredients: ['ing-flour', 'food-milk', 'food-egg'],
    forbid: ['ing-butter', 'ing-sugar', 'ing-salt'],
  },
  {
    id: 'pizza',
    ingredients: ['ing-flour', 'vegetables-tomato', 'food-cheese'],
    forbid: ['food-pizza', 'vegetables-onion', 'vegetables-pepper', 'ing-salt', 'vegetables-broccoli'],
  },
  {
    id: 'salad',
    ingredients: ['vegetables-tomato', 'vegetables-cucumber', 'vegetables-pepper'],
    forbid: ['vegetables-onion', 'ing-salt', 'fruit-lemon', 'vegetables-broccoli'],
  },
  {
    id: 'sandwich',
    ingredients: ['food-bread', 'food-cheese', 'vegetables-tomato'],
    forbid: ['food-sandwich', 'ing-butter', 'vegetables-cucumber', 'food-egg', 'vegetables-pepper', 'vegetables-onion'],
  },
  {
    id: 'lemonade',
    ingredients: ['fruit-lemon', 'ing-sugar', 'ing-water'],
    forbid: ['fruit-orange'],
  },
  {
    id: 'mash',
    ingredients: ['vegetables-potato', 'food-milk', 'ing-butter'],
    forbid: ['ing-salt', 'ing-water', 'vegetables-onion', 'vegetables-pepper'],
  },
  {
    id: 'ricebowl',
    ingredients: ['ing-rice', 'vegetables-pea', 'vegetables-carrot'],
    forbid: ['ing-water', 'ing-salt', 'vegetables-onion', 'vegetables-pepper', 'vegetables-broccoli'],
  },
  {
    id: 'vegetablesoup',
    ingredients: ['vegetables-carrot', 'vegetables-potato', 'vegetables-pea', 'vegetables-onion'],
    forbid: ['food-soup', 'ing-salt', 'ing-water', 'vegetables-pepper', 'vegetables-broccoli', 'vegetables-tomato', 'vegetables-pumpkin'],
  },
  {
    id: 'fruitsalad',
    ingredients: ['fruit-apple', 'fruit-banana', 'fruit-strawberry', 'fruit-grapes'],
    forbid: ['fruit-pear', 'fruit-orange', 'fruit-cherry', 'fruit-pineapple', 'fruit-watermelon', 'fruit-lemon', 'ing-sugar'],
  },
  {
    id: 'cookies',
    ingredients: ['ing-flour', 'ing-butter', 'ing-sugar', 'ing-chocolate'],
    forbid: ['food-cookie', 'food-egg', 'food-milk', 'ing-salt'],
  },
  {
    id: 'cake',
    ingredients: ['ing-flour', 'food-egg', 'ing-sugar', 'ing-butter', 'fruit-strawberry'],
    forbid: ['food-cake', 'food-milk', 'ing-salt', 'ing-chocolate', 'fruit-cherry'],
  },
  // ── the second six ──────────────────────────────────────────────────────────────────────────
  // All four- and five-ingredient dishes, and all built from ingredients that already had art.
  // The first twelve left the top of the ladder with three recipes to draw from, which is why a
  // dish could come back two rounds later — or, when a band emptied, the very next round.
  {
    id: 'pumpkinsoup',
    ingredients: ['vegetables-pumpkin', 'vegetables-onion', 'vegetables-potato', 'food-milk'],
    forbid: ['food-soup', 'ing-salt', 'ing-water', 'vegetables-carrot', 'vegetables-pepper'],
  },
  {
    id: 'pastasalad',
    ingredients: ['ing-noodles', 'vegetables-tomato', 'vegetables-cucumber', 'vegetables-pepper'],
    forbid: ['vegetables-onion', 'food-cheese', 'ing-salt'],
  },
  {
    id: 'smoothie',
    ingredients: ['fruit-banana', 'fruit-strawberry', 'food-milk', 'ing-sugar'],
    forbid: ['fruit-apple', 'fruit-grapes', 'fruit-cherry', 'fruit-orange', 'fruit-pear', 'ing-water'],
  },
  {
    id: 'bakedpotato',
    ingredients: ['vegetables-potato', 'ing-butter', 'food-cheese', 'ing-salt'],
    forbid: ['food-milk', 'vegetables-onion', 'vegetables-pepper'],
  },
  {
    id: 'omelette',
    ingredients: ['food-egg', 'food-milk', 'food-cheese', 'vegetables-pepper'],
    forbid: ['ing-butter', 'ing-salt', 'vegetables-onion', 'vegetables-tomato'],
  },
  {
    id: 'muffins',
    ingredients: ['ing-flour', 'food-egg', 'ing-sugar', 'ing-butter', 'fruit-banana'],
    forbid: ['food-cake', 'food-cookie', 'food-milk', 'ing-salt', 'ing-chocolate', 'fruit-strawberry'],
  },
  // ── the two every kitchen has ───────────────────────────────────────────────────────────────
  {
    id: 'fries',
    ingredients: ['vegetables-potato', 'ing-oil', 'ing-salt'],
    forbid: ['vegetables-tomato', 'ing-butter', 'food-cheese', 'vegetables-pepper'],
  },
  {
    id: 'burger',
    ingredients: ['food-bread', 'ing-meat', 'food-cheese', 'vegetables-tomato'],
    forbid: ['food-sandwich', 'vegetables-onion', 'vegetables-cucumber', 'food-egg', 'ing-butter', 'ing-salt'],
  },

  // ── 🇯🇵 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'sushi',
    langs: ['ja'],
    ingredients: ['ing-rice', 'ing-fish', 'ing-seaweed'],
    forbid: ['vegetables-cucumber', 'ing-salt', 'ing-water', 'ing-oil'],
  },
  {
    id: 'ramen',
    langs: ['ja'],
    ingredients: ['ing-noodles', 'food-egg', 'vegetables-onion'],
    // Everything a bowl of ramen plausibly carries — and note that corn, one of the commonest
    // toppings of all, is already unavailable everywhere because it is a life (NEVER_DEAL).
    forbid: ['ing-salt', 'ing-water', 'ing-meat', 'ing-seaweed', 'vegetables-carrot', 'ing-oil'],
  },
  {
    id: 'curryrice',
    langs: ['ja'],
    ingredients: ['ing-rice', 'vegetables-carrot', 'vegetables-potato', 'vegetables-onion'],
    forbid: ['ing-meat', 'ing-salt', 'ing-water', 'vegetables-pepper', 'food-milk', 'ing-oil'],
  },

  // ── 🇰🇷 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'bibimbap',
    langs: ['ko'],
    ingredients: ['ing-rice', 'vegetables-carrot', 'food-egg', 'vegetables-cucumber'],
    forbid: ['ing-meat', 'ing-seaweed', 'vegetables-pepper', 'vegetables-onion', 'ing-salt', 'ing-oil'],
  },
  {
    id: 'gimbap',
    langs: ['ko'],
    ingredients: ['ing-rice', 'ing-seaweed', 'vegetables-carrot', 'food-egg'],
    forbid: ['vegetables-cucumber', 'ing-salt', 'ing-oil', 'ing-fish', 'ing-meat'],
  },
  {
    id: 'japchae',
    langs: ['ko'],
    ingredients: ['ing-noodles', 'vegetables-carrot', 'vegetables-onion', 'food-egg'],
    forbid: ['vegetables-pepper', 'ing-seaweed', 'ing-salt', 'ing-oil', 'ing-meat', 'vegetables-cucumber'],
  },

  // ── 🇹🇭 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'padthai',
    langs: ['th'],
    ingredients: ['ing-noodles', 'food-egg', 'vegetables-onion', 'ing-oil'],
    forbid: ['vegetables-carrot', 'ing-fish', 'ing-meat', 'ing-salt', 'fruit-lemon', 'vegetables-pepper'],
  },
  {
    id: 'tomyum',
    langs: ['th'],
    ingredients: ['ing-water', 'fruit-lemon', 'vegetables-pepper', 'vegetables-onion'],
    forbid: ['ing-fish', 'ing-meat', 'ing-salt', 'vegetables-tomato', 'food-soup', 'ing-oil'],
  },
  {
    id: 'mangostickyrice',
    langs: ['th'],
    ingredients: ['ing-rice', 'ing-mango', 'food-milk', 'ing-sugar'],
    forbid: ['ing-butter', 'ing-salt', 'fruit-banana', 'fruit-strawberry', 'ing-water'],
  },

  // ── 🇫🇷 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'croissant',
    langs: ['fr'],
    ingredients: ['ing-flour', 'ing-butter', 'food-milk', 'ing-sugar'],
    forbid: ['food-egg', 'ing-salt', 'ing-chocolate', 'ing-water'],
  },
  {
    id: 'quiche',
    langs: ['fr'],
    ingredients: ['ing-flour', 'food-egg', 'food-milk', 'food-cheese'],
    forbid: ['ing-butter', 'ing-salt', 'vegetables-onion', 'vegetables-tomato', 'vegetables-pepper', 'ing-meat'],
  },
  {
    id: 'onionsoup',
    langs: ['fr'],
    ingredients: ['vegetables-onion', 'food-bread', 'food-cheese', 'ing-water'],
    forbid: ['food-soup', 'ing-butter', 'ing-salt', 'vegetables-potato', 'food-milk', 'vegetables-pepper'],
  },

  // ── 🇩🇪 (and 🇨🇭, which reads German's book — see recipesFor) ─────────────────────────────────
  {
    id: 'brezel',
    langs: ['de'],
    ingredients: ['ing-flour', 'ing-water', 'ing-salt'],
    forbid: ['ing-sugar', 'ing-butter', 'food-milk', 'food-egg'],
  },
  {
    id: 'kaesespaetzle',
    langs: ['de'],
    ingredients: ['ing-flour', 'food-egg', 'food-cheese', 'vegetables-onion'],
    forbid: ['ing-butter', 'food-milk', 'ing-salt', 'vegetables-pepper', 'ing-meat'],
  },
  {
    id: 'applestrudel',
    langs: ['de'],
    ingredients: ['ing-flour', 'fruit-apple', 'ing-sugar', 'ing-butter'],
    forbid: ['food-milk', 'food-egg', 'ing-salt', 'fruit-pear', 'food-cake'],
  },

  // ── 🇪🇸 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'paella',
    langs: ['es'],
    ingredients: ['ing-rice', 'ing-fish', 'vegetables-pepper', 'vegetables-onion'],
    forbid: ['ing-meat', 'ing-salt', 'vegetables-tomato', 'ing-oil', 'vegetables-pea', 'ing-water'],
  },
  {
    id: 'tortillapatatas',
    langs: ['es'],
    ingredients: ['vegetables-potato', 'food-egg', 'vegetables-onion', 'ing-oil'],
    forbid: ['ing-salt', 'vegetables-pepper', 'food-cheese', 'food-milk', 'ing-meat'],
  },
  {
    id: 'churros',
    langs: ['es'],
    ingredients: ['ing-flour', 'ing-sugar', 'ing-oil', 'ing-chocolate'],
    forbid: ['ing-butter', 'food-egg', 'food-milk', 'ing-salt'],
  },

  // ── 🇮🇹 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'lasagne',
    langs: ['it'],
    ingredients: ['ing-noodles', 'vegetables-tomato', 'food-cheese', 'ing-meat'],
    forbid: ['vegetables-onion', 'ing-butter', 'ing-salt', 'food-milk', 'vegetables-pepper', 'vegetables-carrot'],
  },
  {
    id: 'risotto',
    langs: ['it'],
    ingredients: ['ing-rice', 'vegetables-onion', 'ing-butter', 'food-cheese'],
    forbid: ['ing-water', 'ing-salt', 'vegetables-pepper', 'food-milk', 'ing-meat', 'vegetables-carrot'],
  },
  {
    id: 'gelato',
    langs: ['it'],
    ingredients: ['food-milk', 'ing-sugar', 'fruit-strawberry'],
    forbid: ['food-icecream', 'ing-butter', 'food-egg', 'ing-chocolate', 'fruit-banana', 'fruit-cherry'],
  },

  // ── 🇳🇱 ─────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'stroopwafel',
    langs: ['nl'],
    ingredients: ['ing-flour', 'ing-butter', 'ing-sugar'],
    forbid: ['food-egg', 'food-milk', 'ing-salt', 'ing-chocolate', 'food-cookie'],
  },
  {
    id: 'poffertjes',
    langs: ['nl'],
    ingredients: ['ing-flour', 'food-milk', 'food-egg', 'ing-sugar'],
    forbid: ['ing-butter', 'ing-salt', 'ing-chocolate', 'fruit-strawberry'],
  },
  {
    id: 'erwtensoep',
    langs: ['nl'],
    ingredients: ['vegetables-pea', 'vegetables-potato', 'vegetables-onion', 'ing-water'],
    forbid: ['food-soup', 'ing-salt', 'ing-meat', 'vegetables-pepper', 'food-milk', 'vegetables-carrot'],
  },

  // ── 🇨🇭 the one shelf keyed on a FULL tag rather than a base code ────────────────────────────
  // Swiss German reads Germany's book AND these; see recipesFor for why the rule takes both.
  {
    id: 'roesti',
    langs: ['de-CH'],
    ingredients: ['vegetables-potato', 'ing-butter', 'ing-salt'],
    forbid: ['ing-oil', 'vegetables-onion', 'food-cheese', 'vegetables-pepper', 'food-egg'],
  },
  {
    id: 'fondue',
    langs: ['de-CH'],
    // Cheese, bread, and the garlic the caquelon is rubbed with. NOT milk — a fondue does not take
    // any, and the first draft's "milk where an adult recipe has wine" was inventing a recipe
    // rather than simplifying one. Wine stays out for the obvious reason, and garlic is the part
    // that was actually missing. The board asks what goes IN the pot, so the fork is not an
    // ingredient.
    ingredients: ['food-cheese', 'food-bread', 'ing-garlic'],
    forbid: ['food-milk', 'ing-water', 'ing-butter', 'ing-flour', 'ing-salt', 'vegetables-potato', 'vegetables-onion'],
  },
  {
    id: 'zopf',
    langs: ['de-CH'],
    ingredients: ['ing-flour', 'ing-butter', 'food-milk', 'food-egg'],
    forbid: ['ing-sugar', 'ing-salt', 'ing-water', 'ing-chocolate', 'food-bread'],
  },
];

/**
 * The book a given language cooks from: the shared recipes, plus its language's own, plus its
 * region's own where it has any.
 *
 * **Both the full tag and the base code match**, which is the whole trick. `de` gets Brezel,
 * Käsespätzle and Apfelstrudel; `de-CH` gets those *and* Rösti, Fondue and Zopf, because a Swiss
 * child is not a German child with three dishes taken away — they know both kitchens, and the one
 * they will not find anywhere else is theirs. `en-GB` still reads English's book, since no recipe
 * claims that tag.
 *
 * Written this way rather than as "region replaces language" for a reason worth keeping: replacing
 * would mean every new German dish silently skips Switzerland, and nobody would notice until a
 * Swiss child never met it.
 */
export function recipesFor(lang: string): Recipe[] {
  const tag = lang.toLowerCase();
  const base = tag.split('-')[0];
  return RECIPES.filter(
    (r) => r.langs === undefined || r.langs.some((l) => {
      const want = l.toLowerCase();
      return want === tag || want === base;
    }),
  );
}

/** Look one up by id. */
export function recipeById(id: string): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

// ── art and words ───────────────────────────────────────────────────────────────────────────────

/** Where this mode's own art is served from. Precached with everything else (vite.config.ts). */
export const RECIPE_ART_DIR = '/images/recipe';

/** True for the eight ingredients this mode owns; false for a picture-word id. */
export function isOwnIngredient(id: string): boolean {
  return id.startsWith('ing-');
}

/** Phaser texture key for an ingredient tile. Prefixed so it can never collide with a word icon. */
export function ingredientIconKey(id: string): string {
  return `ri_${id}`;
}

/**
 * URL an ingredient's picture is loaded from — the shipped picture-word icon where the pool has
 * the word, this mode's own art where it does not.
 */
export function ingredientIconUrl(id: string): string {
  return isOwnIngredient(id) ? `${RECIPE_ART_DIR}/${id}.webp` : `/images/words/${id}.webp`;
}

/** Phaser texture key for a finished dish. */
export function dishIconKey(recipeId: string): string {
  return `rd_${recipeId}`;
}

export function dishIconUrl(recipeId: string): string {
  return `${RECIPE_ART_DIR}/dish-${recipeId}.webp`;
}

/**
 * The i18n key an ingredient's NAME comes from, or null when the picture pool owns the word.
 *
 * Null is the common case and the good one: the caller then reads the pool, which is per-language,
 * reviewed, and the same text the child meets in the picture lesson.
 */
export function ingredientI18nKey(id: string): string | null {
  return isOwnIngredient(id) ? `recipe.ing.${id.slice('ing-'.length)}` : null;
}

/** The i18n key a dish's name comes from. Always this mode's own — dishes are never pool words. */
export function dishI18nKey(recipeId: string): string {
  return `recipe.dish.${recipeId}`;
}
